import { useState } from "react";
import { pluralText, useT } from "../i18n/hooks.ts";
import { getWarningTranslation } from "../engine/engineWarnings.ts";

/**
 * Surfaces `SimulationResults.warnings` in the UI. Before this existed, the
 * engine already collected real warnings (malformed rows, unsupported
 * elements, routing-method limitations...) on every run, but nothing
 * displayed them — a model could silently lose pieces and look complete.
 * Collapsed to a single summary line by default since a real model can
 * produce many warnings at once.
 */
export function SimulationWarningsBanner({
  warnings,
  onDismiss,
}: {
  warnings: string[];
  onDismiss: () => void;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  if (warnings.length === 0) return null;
  return (
    <div className="simulation-warnings-banner" role="alert">
      <div className="simulation-warnings-banner-row">
        <button
          type="button"
          className="simulation-warnings-banner-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {pluralText(
            t,
            warnings.length,
            "simulation.warningsTitleOne",
            "simulation.warningsTitle"
          )}
        </button>
        <button
          type="button"
          className="simulation-warnings-banner-dismiss"
          aria-label={t("common.close")}
          onClick={onDismiss}
        >
          ×
        </button>
      </div>
      {expanded && (
        <ul className="simulation-warnings-banner-list">
          {warnings.map((warning) => {
            // Every warning SHOULD be registered by now (engineWarnings.ts's
            // own header) — the raw English fallback only matters for one
            // never routed through `warningText`, which would be a bug to
            // fix at its own source, not a case to design elegant UI for.
            const translation = getWarningTranslation(warning);
            return (
              <li key={warning}>
                {translation ? t(translation.key, translation.params) : warning}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
